import {
  type DataGridRef,
  type DialogRef,
  DynamicForm,
  type DynamicFormButtonSetting,
  type DynamicFormRef,
  type JsonDataItem,
  type JsonDataWrapper,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
} from '@myosh/odin-components';
import { cloneDeep, debounce } from 'lodash';
import { type Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Subject, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import type { ExtendedDynamicFormSettings } from '../../@types/forms';
import useDynamicFormNotifier from '../../hooks/use-dynamic-form-notifier';
import { useUserAccess } from '../../hooks/use-user-access';
import i18next from '../../i18n';
import { useHierarchyTypesQuery } from '../../redux/services/hierarchy';
import { getDataGridReference } from '../../services/data-grid.service';
import { forceAssert } from '../../utils/type-guards';
import { HierarchiesPermissions } from '../../utils/user.permissions';
import CloseActiveItemButton from '../common/close-active-item-button';
import { FormLoading } from '../form/form-loading.component';
import { FormTitle } from '../form/form-title.component';
import { getHierarchyFormSettings } from './hierarchy-form-settings';
import { getHierarchyFieldOptions } from './hierarchy-utils';

export interface HierarchyDetailsProps {
  hierarchyId: number;
  title: string;
  editable?: boolean;
  onClose: (id: string) => void;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}

interface HierarchyDetailsRef {
  submitForm?: () => void;
}

const saveButton: DynamicFormButtonSetting = {
  name: 'save',
  text: i18next.t('save'),
  htmlType: 'submit',
  type: 'primary',
  variant: 'alternative',
};

const HierarchyDetails = (props: HierarchyDetailsProps, ref: Ref<HierarchyDetailsRef>) => {
  const { hierarchyId, title, editable, onClose, onCloseActiveRecordConfirmationModalRef } = props;

  const [hierarchy, setHierarchy] = useState<Record<string, unknown>>();
  const [formSettings, setFormSettings] = useState<ExtendedDynamicFormSettings>();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const dynamicFormId = useRef(v4());

  const { data, isFetching } = useHierarchyTypesQuery({});

  const { notifyDirty } = useDynamicFormNotifier(dynamicFormId.current);

  const { readOnly, editAccess } = useUserAccess({
    initialAccess: Boolean(hierarchyId) === editable,
    permissions: {
      MODIFY: [HierarchiesPermissions.HIERARCHIES_MODIFY, HierarchiesPermissions.HIERARCHIES_CREATE],
    },
  });

  useEffect(() => {
    if (data && !isFetching) {
      const currentHierarchyType = data.find((d) => d.id === hierarchyId);
      const hierarchyData = cloneDeep(currentHierarchyType);
      if (hierarchyData) {
        const transformedData: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(hierarchyData)) {
          switch (key) {
            case 'archived':
            case 'dependsOnParent':
            case 'external':
            case 'mandatoryForNewForms':
            case 'mandatoryForUserRecords':
              transformedData[key] = {
                value: value,
                text: '',
              };
              break;
            default:
              transformedData[key] = value;
              break;
          }
        }
        setHierarchy(transformedData);
      }
    }
  }, [data, isFetching, hierarchyId]);

  useEffect(() => {
    setFormSettings(getHierarchyFormSettings(readOnly, hierarchyId));
  }, [readOnly, hierarchyId]);

  useEffect(() => {
    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          dynamicFormReference.current?.submitForm(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    submitForm: dynamicFormReference.current?.submitForm,
  }));

  const onFormSubmit = () => {};

  const onFieldChanged = debounce(() => notifyDirty(dynamicFormReference.current), 500);

  const onCloseHierarchyDetails = () => {
    onClose(dynamicFormId.current);
  };

  const dataRetrieval: OdinDataRetrieval = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType || 'OPTIONGROUP' === options?.fieldType) {
        await getHierarchyFieldOptions(subscriber, options);
      } else {
        subscriber.sendData();
      }
    },
  };

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />

          <div className="flex min-w-min justify-end">
            <CloseActiveItemButton onClick={onCloseHierarchyDetails} />
          </div>
        </div>
      </div>
      <div className="admin-form-default">
        {!hierarchy && <FormLoading />}
        {hierarchy && formSettings && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={forceAssert<JsonDataItem>(hierarchy)}
            dataRetrieval={dataRetrieval}
            settings={formSettings}
            buttons={[saveButton]}
            buttonsLocation={0}
            readOnly={!editAccess}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
    </>
  );
};
export default forwardRef(HierarchyDetails);
